/**
 * The print-ready welcome-pack kit — dashboard delivery.
 *
 * `GET /api/print-kit` never accepts a slug from this page; the backend
 * resolves the caller's own website and answers `{ status, manifest, identity }`.
 * `identity` is the SAME payload the invite page's welcome-pack section gets
 * from `GET /api/invite/:token/identity` (see `../pack/identity.ts`), so the
 * thumbnails here are rendered by the very same live components
 * (`CardFront`/`CardBack`, `DoorHanger`/`CarMagnet`/`YardSign`) at a reduced
 * `w`/`h` — the "reflow" fit documented on those components' props, not a
 * screenshot and not a re-drawn mock.
 *
 * Three states: `none` (never generated — one button), `pending` (a
 * generation is running on the build service, which can take a couple of
 * minutes for a full kit — poll for the manifest to land), `ready` (one card
 * per piece, a live thumbnail, its physical size, and a download button).
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CARD_H,
  CARD_W,
  CardBack,
  CardFront,
  useGoogleFonts,
  CarMagnet,
  DoorHanger,
  HANGER_H,
  HANGER_W,
  MAGNET_H,
  MAGNET_W,
  PackShowcase,
  SIGN_H,
  SIGN_W,
  YardSign,
  identityToPack,
} from '@foundlio/pack';
import { Empty, Eyebrow, PageHeader, Panel, PanelBody, PanelHeader, Pill, Tag } from '../ui/kit';
import { printKitService, type KitPiece, type PieceId, type PrintKitState } from '../../services/printKitService';

/** How long the "Generate my kit" flow polls before giving up and saying so. */
const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 30; // 2.5 minutes — a full 4-piece kit renders in well under that

const DownloadIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M7.5 12l4.5 4.5m0 0 4.5-4.5m-4.5 4.5V3" />
  </svg>
);

const RefreshIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.75}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M3.985 14.652H-.007m4.992 0v4.992m0-4.992 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.183m0-4.993v4.992" />
  </svg>
);

const Spinner = () => (
  <span
    aria-hidden
    className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-zinc-300 border-t-primary"
  />
);

const PIECE_PHYSICAL: Record<PieceId, string> = {
  card: '3.5 × 2 in',
  hanger: '4.25 × 11 in',
  magnet: '18 × 12 in',
  sign: '24 × 18 in',
};

/**
 * Every piece is fixed-px inside (a print sheet, not a responsive layout):
 * padding, font sizes, the QR's pixel size and (on the hanger) the die-cut
 * hole's own offset are all constants, not proportions of `w`/`h`. Passing a
 * small `w`/`h` override — the "reflow" fit meant for re-shaping a piece to a
 * differently-proportioned box — shrinks the CONTAINER around that fixed
 * content and clips it. A CSS `transform: scale()` shrinks the whole painted
 * result instead, the same technique `PackShowcase`'s `Piece` wrapper already
 * uses for the invite's product shot, and is the only way to make one of
 * these small without breaking it.
 */
function Scaled({ w, h, scale, children }: { w: number; h: number; scale: number; children: React.ReactNode }) {
  return (
    <div style={{ width: Math.round(w * scale), height: Math.round(h * scale) }}>
      <div style={{ width: w, height: h, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {children}
      </div>
    </div>
  );
}

/** Scale factors, tuned so every piece stays legible while keeping the size
 *  ORDER a real kit has (card smallest, magnet/sign the big-format pieces). */
const PIECE_SCALE: Record<PieceId, number> = {
  card: 0.52,
  hanger: 0.46,
  magnet: 0.56,
  sign: 0.44,
};

type PackBusiness = ReturnType<typeof identityToPack>;

/** The card alone gets a front/back toggle — CardFront and CardBack are two
 *  separate components, and side-by-side at a legible scale is wider than a
 *  grid column comfortably holds. */
function CardThumb({ b, domain }: { b: PackBusiness; domain: string }) {
  const [face, setFace] = useState<'front' | 'back'>('front');
  const shared = { guides: false, nameMode: 'auto' as const, logoMode: 'auto' as const, layout: 'center' as const };
  return (
    <div className="flex flex-col items-center gap-3">
      <Scaled w={CARD_W} h={CARD_H} scale={PIECE_SCALE.card}>
        {face === 'front' ? (
          <CardFront b={b} {...shared} />
        ) : (
          <CardBack b={b} {...shared} qrUrl={`https://${domain}/review`} />
        )}
      </Scaled>
      <div className="flex overflow-hidden rounded-full ring-1 ring-zinc-200">
        {(['front', 'back'] as const).map(f => (
          <button
            key={f}
            type="button"
            onClick={() => setFace(f)}
            className={`px-3 py-1 font-mono text-[10px] uppercase tracking-eyebrow transition-colors ${
              face === f ? 'bg-primary text-white' : 'bg-white text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            {f}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Live thumbnail for one manifest piece, or a placeholder when there is no identity to render it from. */
function PieceThumb({ piece, business, domain }: { piece: KitPiece; business: PackBusiness | null; domain: string }) {
  if (!business) {
    return (
      <div className="flex h-[190px] w-[240px] items-center justify-center rounded-lg bg-zinc-100 font-mono text-[11px] uppercase tracking-eyebrow text-zinc-400">
        Preview unavailable
      </div>
    );
  }

  if (piece.id === 'card') return <CardThumb b={business} domain={domain} />;

  const shared = { guides: false, nameMode: 'auto' as const, logoMode: 'auto' as const };
  const site = `https://${domain}`;

  const node =
    piece.id === 'hanger' ? (
      <Scaled w={HANGER_W} h={HANGER_H} scale={PIECE_SCALE.hanger}>
        <DoorHanger b={business} {...shared} qrUrl={site} />
      </Scaled>
    ) : piece.id === 'magnet' ? (
      <Scaled w={MAGNET_W} h={MAGNET_H} scale={PIECE_SCALE.magnet}>
        <CarMagnet b={business} {...shared} />
      </Scaled>
    ) : (
      <Scaled w={SIGN_W} h={SIGN_H} scale={PIECE_SCALE.sign}>
        <YardSign b={business} {...shared} qrUrl={site} />
      </Scaled>
    );

  return <div className="flex min-h-[190px] items-center justify-center">{node}</div>;
}

function PieceCard({
  piece,
  business,
  domain,
  onDownload,
  downloading,
}: {
  piece: KitPiece;
  business: PackBusiness | null;
  domain: string;
  onDownload: () => void;
  downloading: boolean;
}) {
  return (
    <Panel className="overflow-hidden">
      <PanelHeader
        title={piece.label}
        actions={
          piece.logoOk === false ? (
            <Tag tone="warn" dot>
              Logo missing
            </Tag>
          ) : undefined
        }
      />
      <PanelBody className="flex flex-col items-center gap-4">
        <PieceThumb piece={piece} business={business} domain={domain} />
        <div className="flex w-full items-center justify-between border-t border-zinc-200 pt-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">Finished size</div>
            <div className="mt-0.5 text-sm font-medium text-primary">{PIECE_PHYSICAL[piece.id]}</div>
          </div>
          <Pill tone="outline" size="sm" onClick={onDownload} disabled={downloading}>
            {downloading ? <Spinner /> : <DownloadIcon />}
            {downloading ? 'Preparing…' : 'Download PDF'}
          </Pill>
        </div>
      </PanelBody>
    </Panel>
  );
}

export default function PrintKitPage() {
  const queryClient = useQueryClient();
  const [downloadingId, setDownloadingId] = useState<PieceId | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const pollsRef = useRef(0);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['print-kit'],
    queryFn: () => printKitService.getStatus(),
    staleTime: 30 * 1000,
  });

  const status = data?.status;
  const identity = data?.identity ?? null;
  const business = identity ? identityToPack(identity, identity.owner ?? '') : null;
  useGoogleFonts([business?.fonts?.heading, business?.fonts?.logo]);

  // Poll while a generation is in flight — capped, so a stuck build service
  // does not spin this tab forever.
  useEffect(() => {
    if (status !== 'pending') {
      pollsRef.current = 0;
      return;
    }
    if (pollsRef.current >= MAX_POLLS) return;
    const timer = setTimeout(() => {
      pollsRef.current += 1;
      refetch();
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [status, data, refetch]);

  const startGeneration = async () => {
    setRegenerating(true);
    try {
      await printKitService.regenerate();
      pollsRef.current = 0;
      queryClient.setQueryData<PrintKitState>(['print-kit'], old =>
        old ? { ...old, status: 'pending', manifest: null } : old
      );
    } finally {
      setRegenerating(false);
    }
  };

  const handleDownload = async (piece: KitPiece) => {
    setDownloadingId(piece.id);
    try {
      const businessSlug = identity?.slug ?? 'foundlio';
      await printKitService.downloadPiece(piece.id, `${businessSlug}-${piece.id}.pdf`);
    } finally {
      setDownloadingId(null);
    }
  };

  const pollsExhausted = status === 'pending' && pollsRef.current >= MAX_POLLS;

  return (
    <div>
      <PageHeader
        eyebrow="Marketing"
        title="Print kit"
        description="Business cards, a door hanger and a car magnet or yard sign, in the same brand as your website."
        actions={
          status === 'ready' ? (
            <Pill tone="outline" size="sm" onClick={startGeneration} disabled={regenerating}>
              {regenerating ? <Spinner /> : <RefreshIcon />}
              Regenerate
            </Pill>
          ) : undefined
        }
      />

      {isLoading && (
        <Panel className="p-10 text-center">
          <Eyebrow>Loading your print kit…</Eyebrow>
        </Panel>
      )}

      {isError && !isLoading && (
        <Empty
          framed
          title="Couldn't load your print kit"
          description="Something went wrong talking to the server. Try again in a moment."
          action={
            <Pill tone="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Pill>
          }
        />
      )}

      {!isLoading && !isError && status === 'none' && (
        <Empty
          framed
          eyebrow="Not generated yet"
          title="Build your print kit"
          description="Cards, a door hanger and a car magnet or yard sign — rendered straight from your site's real logo, colours and type, ready for any print shop."
          action={
            <Pill tone="solid" onClick={startGeneration} disabled={regenerating}>
              {regenerating ? <Spinner /> : null}
              Generate my print kit
            </Pill>
          }
        />
      )}

      {!isLoading && !isError && status === 'pending' && (
        <Empty
          framed
          eyebrow={pollsExhausted ? 'Taking longer than expected' : 'Generating'}
          title={pollsExhausted ? 'Still working on it' : 'Building your print kit…'}
          description={
            pollsExhausted
              ? "This is taking longer than usual. It's still running on our side — check back in a few minutes, or try again."
              : 'Rendering every piece at print resolution. This takes a minute or two for a full kit.'
          }
          action={
            <Pill tone="outline" size="sm" onClick={() => refetch()}>
              {pollsExhausted ? <RefreshIcon /> : <Spinner />}
              {pollsExhausted ? 'Check again' : 'Checking…'}
            </Pill>
          }
        />
      )}

      {!isLoading && !isError && status === 'ready' && data?.manifest && (
        <>
          {business && (
            <Panel className="mb-6 overflow-hidden">
              <PanelHeader title="Your pack" />
              <PanelBody>
                <PackShowcase b={business} domain={identity?.domain ?? ''} />
              </PanelBody>
            </Panel>
          )}
          <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
            {data.manifest.pieces.map(piece => (
              <PieceCard
                key={piece.id}
                piece={piece}
                business={business}
                domain={identity?.domain ?? ''}
                onDownload={() => handleDownload(piece)}
                downloading={downloadingId === piece.id}
              />
            ))}
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase leading-relaxed tracking-eyebrow text-zinc-400">
            0.125in bleed, crop marks, RGB — hand these to any print shop.
          </p>
        </>
      )}
    </div>
  );
}
